// SPDX-License-Identifier: AGPL-3.0-or-later

import type {StreamActivity} from '@app/features/gateway/types/GatewayPresenceTypes';
import MemberSidebar from '@app/features/member/state/MemberSidebar';
import Presence from '@app/features/presence/state/Presence';
import {compareStructural, reaction} from 'mobx';
import {useEffect, useState} from 'react';

interface UseMemberListStreamActivityOptions {
	guildId: string;
	channelId: string;
	userId: string;
}

export function resolveMemberListStreamActivity({
	guildId,
	channelId,
	userId,
}: UseMemberListStreamActivityOptions): StreamActivity | null {
	const memberListActivity = guildId ? MemberSidebar.getStreamActivity(guildId, channelId, userId) : undefined;
	return memberListActivity !== undefined ? memberListActivity : Presence.getStreamActivity(userId);
}

// A reaction instead of an observer read: every member list update replaces the list state, and a row only
// re-renders when its own stream activity changes.
export function useMemberListStreamActivity({
	guildId,
	channelId,
	userId,
}: UseMemberListStreamActivityOptions): StreamActivity | null {
	const [activity, setActivity] = useState(() => resolveMemberListStreamActivity({guildId, channelId, userId}));
	useEffect(
		() =>
			reaction(() => resolveMemberListStreamActivity({guildId, channelId, userId}), setActivity, {
				equals: compareStructural,
				fireImmediately: true,
			}),
		[guildId, channelId, userId],
	);
	return activity;
}
